import AuthenticatedLayout from "@/components/AuthenticatedLayout";
import BackToDashboard from "@/components/BackToDashboard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatAgoFromISO, formatDate } from "@/lib/formatters";
import { PageProps } from "@/types/PageProps";
import Adjustment from "@/types/serializers/Adjustment";
import { Head } from "@inertiajs/react";
import { AlertCircle, DownloadIcon } from "lucide-react";

type Props = PageProps & {
  adjustments: Adjustment[];
};

export default function Component({ adjustments, user, errors, notice }: Props) {
  const filteredAdjustments = adjustments.filter((adjustment) => {
    if (!adjustment.read_only) {
      return true;
    }

    if (adjustment.most_recent_pdf_url) {
      return true;
    }

    return false;
  });

  return (
    <AuthenticatedLayout user={user} errors={errors} notice={notice}>
      <Head title="Adjustments" />

      <div className="py-4">
        <BackToDashboard />
      </div>
      <div className="flex items-start mx-auto space-x-4 mx-auto">
        <div className="flex-1 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Adjustment Reports</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Address</TableHead>
                    <TableHead></TableHead>
                    <TableHead>Effective Date</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAdjustments.map((adjustment) => (
                    <TableRow key={adjustment.public_id}>
                      <TableCell>
                        <a href={`/adjustments/${adjustment.public_id}`} className="underline">
                          {adjustment.address}
                        </a>
                      </TableCell>
                      <TableCell>
                        {adjustment.read_only && <Badge variant="secondary">Read-only</Badge>}
                      </TableCell>
                      <TableCell>
                        {adjustment.effective_date &&
                          formatDate(new Date(adjustment.effective_date).getTime())}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatAgoFromISO(adjustment.created_at)}
                      </TableCell>
                      <TableCell>
                        {adjustment.current_adjustment_pdf_url && (
                          <a
                            href={adjustment.current_adjustment_pdf_url}
                            target="_blank"
                            className={buttonVariants({ variant: "outline", size: "xs" })}
                          >
                            Download PDF <DownloadIcon className="w-4 h-4" />
                          </a>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
        {
          <Alert variant="muted" className="max-w-sm">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle className="font-medium">Retention Policy</AlertTitle>
            <AlertDescription>
              After 30 days, your reports will become read-only. You'll still be able to access the
              PDF summary.
            </AlertDescription>
          </Alert>
        }
      </div>
    </AuthenticatedLayout>
  );
}
